import type { MouseEvent } from "react";
import clsx from "clsx";
import type { AuditScope } from "@/services/api/guardiansv2/types";
import { useOsNavigate } from "@/components/ui/os-layout";
import { entityDeepLink } from "./entityDeepLink";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";

export interface AuditScopeLinksProps {
  scope: AuditScope;
  labels: ScopeLabelCache;
  /** Compact single-line chips for list cards. */
  compact?: boolean;
  className?: string;
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/**
 * Clickable mission / task / interaction scope line with resolved labels.
 */
export function AuditScopeLinks({
  scope,
  labels,
  compact = false,
  className = "",
}: AuditScopeLinksProps) {
  const osNavigate = useOsNavigate();

  const missionId = scope.mission_id;
  const taskId = scope.task_id;
  const interactionId = scope.interaction_id;

  if (!missionId && !taskId && !interactionId) {
    return null;
  }

  const missionText =
    labels.missionLabel(missionId) ??
    (missionId ? `Mission ${shortId(missionId)}` : null);
  const taskText =
    labels.taskLabel(taskId) ?? (taskId ? `Task ${shortId(taskId)}` : null);

  const missionLink = missionId
    ? entityDeepLink({ kind: "mission", id: missionId, scope: { mission_id: missionId } })
    : null;
  const taskLink = taskId
    ? entityDeepLink({
        kind: "task",
        id: taskId,
        scope: { mission_id: missionId, task_id: taskId },
      })
    : null;

  const openMission = (e: MouseEvent) => {
    e.stopPropagation();
    missionLink?.run({ osNavigate });
  };

  const openTask = (e: MouseEvent) => {
    e.stopPropagation();
    taskLink?.run({ osNavigate });
  };

  const chipClass = clsx(
    "rounded-md bg-slate-800/80 text-indigo-200 hover:bg-slate-700/90 hover:text-indigo-100 transition-colors",
    compact ? "px-1.5 py-0.5 text-[11px]" : "px-2 py-0.5 text-xs"
  );

  return (
    <div
      className={clsx(
        "flex min-w-0 flex-wrap items-center gap-1.5",
        className
      )}
      data-testid="audit-scope-links"
    >
      {missionId && missionText ? (
        <button type="button" onClick={openMission} className={chipClass}>
          {missionText}
        </button>
      ) : null}
      {taskId && taskText ? (
        <>
          {missionId ? (
            <span className={clsx("text-slate-600", compact ? "text-[10px]" : "text-xs")}>
              /
            </span>
          ) : null}
          <button type="button" onClick={openTask} className={chipClass}>
            {taskText}
          </button>
        </>
      ) : null}
      {interactionId ? (
        <span
          className={clsx(
            "text-slate-500 truncate max-w-[10rem]",
            compact ? "text-[10px]" : "text-xs"
          )}
          title={interactionId}
        >
          {compact ? "Interaction" : `Interaction ${shortId(interactionId)}`}
        </span>
      ) : null}
    </div>
  );
}
